import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ICertificateRecordQuery } from 'app/library/certificate-record/interfaces/certificate-record.query.interface';
import { CertificateRecord } from 'app/library/certificate-record/models/certificate-record';
import { IEnvironment } from 'app/projects/core/src/lib/interfaces/environment.interface';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { EntityService } from 'app/projects/entity/src/lib/services/entity.service';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CertificationProviderStatusEnum } from '../enums/certification-provider-status.enum';
import { CertificationProviderTypeEnum } from '../enums/certification-provider-type.enum';
import { CertificationProviderSearchInterface } from '../interfaces/certification-provider-search.interface';
import { ICertificationProviderQuery } from '../interfaces/certification-provider.query.interface';
import { CertificationProvider } from '../models/certification-provider';
import { CertificationProviderSearchCommand } from '../models/certification-provider-search.command.model';
import { CertificationProviderTeamMemberDownloadCommand } from '../models/certification-provider-team-member-download.command.model';
import { CertificationProviderCommand } from '../models/certification-provider.command.model';

export interface ICertificationProvidersAndTotalCount {
    data: ICertificationProviderQuery[];
    user_ids: number[];
    pagination: {
        number_of_pages: number;
        number_of_results: number;
    };
}

@Injectable()
export class CertificationProviderApiService extends EntityService<ICertificationProviderQuery, CertificationProviderCommand> {
    constructor(http: HttpClient, @Inject(MAXBRAIN_ENVIRONMENT) private environment: IEnvironment) {
        super(http, environment.apiUrl, 'certification-providers');
    }

    getCertificationProvidersSearch(pageSize = 10, pageIndex = 1, search = '', payload: CertificationProviderSearchInterface): Observable<ICertificationProvidersAndTotalCount> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('filter[query]', search);
        }

        const data = new CertificationProviderSearchCommand(payload);

        return this.http.post<ICertificationProvidersAndTotalCount>(`${this.environment.apiUrl}/certificates/search`, data, { params });
    }

    getTeamCertificationProvidersSearch(
        pageSize = 10,
        pageIndex = 1,
        search = '',
        payload: CertificationProviderSearchInterface,
        teamId: string,
        includeSubteams: boolean = false
    ): Observable<ICertificationProvidersAndTotalCount> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (includeSubteams) {
            params = params.set('includeSubteams', true.toString());
        }

        const data = new CertificationProviderSearchCommand(payload);

        return this.http.post<ICertificationProvidersAndTotalCount>(`${this.environment.apiUrl}/teams/${teamId}/certificates/search`, data, { params });
    }

    getTeamMemberertificationProvidersSearch(
        pageSize = 10,
        pageIndex = 1,
        search = '',
        payload: CertificationProviderSearchInterface,
        userId: string
    ): Observable<ICertificationProvidersAndTotalCount> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('filter[query]', search);
        }

        const data = new CertificationProviderSearchCommand(payload);

        return this.http.post<ICertificationProvidersAndTotalCount>(`${this.environment.apiUrl}/users/${userId}/certificates/search`, data, { params });
    }

    downloadCertificatesAdmin(pageSize = 10, pageIndex = 1, search = '', payload: CertificationProviderSearchInterface): Observable<Blob> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('filter[query]', search);
        }

        const data = new CertificationProviderSearchCommand(payload);

        return this.http
            .post(`${this.environment.apiUrl}/certificates/download`, data, {
                params,
                responseType: 'blob',
            })
            .pipe(tap((file) => MaxBrainUtils.downloadFile(file, 'Certificates')));
    }

    downloadCertificatesTeam(
        pageSize = 10,
        pageIndex = 1,
        search = '',
        payload: CertificationProviderSearchInterface,
        teamId: string,
        includeSubteams: boolean = false
    ): Observable<Blob> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (includeSubteams) {
            params = params.set('includeSubteams', true.toString());
        }

        const data = new CertificationProviderSearchCommand(payload);

        return this.http
            .post(`${this.environment.apiUrl}/teams/${teamId}/certificates/download`, data, { params, responseType: 'blob' })
            .pipe(tap((file) => MaxBrainUtils.downloadFile(file, 'Certificates')));
    }

    downloadCertificatesTeamMember(
        pageSize = 10,
        pageIndex = 1,
        search = '',
        payload: CertificationProviderSearchInterface,
        teamId: string,
        userId: string
    ): Observable<Blob> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('filter[query]', search);
        }

        const data = new CertificationProviderTeamMemberDownloadCommand(payload, userId);

        return this.http
            .post(`${this.environment.apiUrl}/teams/${teamId}/certificates/download`, data, { params, responseType: 'blob' })
            .pipe(tap((file) => MaxBrainUtils.downloadFile(file, 'Certificates')));
    }

    getCertificatesToReplace(pageSize = 10, pageIndex = 1, certificateId: string): Observable<ICertificationProvidersAndTotalCount> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        return this.http.get<ICertificationProvidersAndTotalCount>(`${this.environment.apiUrl}/certificates/${certificateId}/replace`, { params });
    }

    replaceCertificate(certificateId: string, certificationId: string): Observable<CertificateRecord> {
        const data = {
            certificate: {
                id: parseInt(certificationId),
            },
        };
        return this.http.post<ICertificateRecordQuery>(`${this.environment.apiUrl}/certificates/${certificateId}/replace`, data).pipe(map((query) => new CertificateRecord(query)));
    }

    getCertificationProviders(pageSize = 10, pageIndex = 1, search = '', types: number[] = [], statuses: number[] = []): Observable<ICertificationProvidersAndTotalCount> {
        let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

        if (search) {
            params = params.set('filter[query]', search);
        }

        if (types) {
            params = params.set('filter[type]', types.toString());
        }

        if (statuses) {
            params = params.set('filter[statuses]', statuses.toString());
        }

        return this.http.get<ICertificationProvidersAndTotalCount>(`${this.environment.apiUrl}/${this.entities}`, { params });
    }

    getAll(): Observable<CertificationProvider[]> {
        return this._getAll().pipe(map((queries) => queries.map((query) => new CertificationProvider(query))));
    }

    create(name: string, type: CertificationProviderTypeEnum): Observable<CertificationProvider> {
        const data = new CertificationProviderCommand(name, type);

        return this._create(data).pipe(map((query) => new CertificationProvider(query)));
    }

    read(id: string): Observable<CertificationProvider> {
        return this._read(id).pipe(map((query) => new CertificationProvider(query)));
    }

    update(name: string, id: string): Observable<CertificationProvider> {
        const data = new CertificationProviderCommand(name);

        return this._update(id, data).pipe(map((query) => new CertificationProvider(query)));
    }

    updateStatus(id: string, status: CertificationProviderStatusEnum): Observable<CertificationProvider> {
        return this.http
            .patch<ICertificationProviderQuery>(`${this.environment.apiUrl}/${this.entities}/${id}/status`, { status })
            .pipe(map((query) => new CertificationProvider(query)));
    }

    delete(id: string): Observable<any> {
        return this._delete(id);
    }
}
